// This file configures the initialization of Sentry on the browser.
// The config you add here will be used whenever a page is visited.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryRewritesTunnelPath"] = undefined;globalThis["SENTRY_RELEASE"] = {"id":"zTvZaWpHOJH2PUUCsNYt8"};globalThis["_sentryBasePath"] = undefined;globalThis["_sentryRewriteFramesAssetPrefixPath"] = "/resources/arm64/20260313-1144-ccc26840c7_store";import * as Sentry from '@sentry/nextjs';

import { Config } from './config';

const isAllowedStage = () => Config.stage === 'alpha' || Config.stage === 'beta' || Config.stage === 'production';
const getInitialSentryOptions = () => {
  if (isAllowedStage()) {
    return {
      sampleRate: 1,
      tracesSampleRate: 0.0007,
      replaysOnErrorSampleRate: 0.000001,
      replaysSessionSampleRate: 0.000001,
    };
  }

  return {
    sampleRate: 0,
    tracesSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    replaysSessionSampleRate: 0,
  };
};

// MEMO: https://sentry.io/answers/handle-hard-navigation-errors-in-nextjs/
function isNextNavigationError(error_message) {
  return error_message.includes('Invariant: attempted to hard navigate to the same URL');
}

// MEMO: suspense 사용시 Server Client의 하이드레이션 이슈
function isSuspenseBoundaryError(error_message) {
  return error_message.includes('This Suspense boundary received an update before it finished hydrating');
}

const EXTRA_KEY = 'ROUTE_TO';

// MEMO: https://docs.sentry.io/platforms/javascript/best-practices/micro-frontends/
const transport = Sentry.makeMultiplexedTransport(Sentry.makeFetchTransport, (args) => {
  const event = args.getEvent();
  if (event && event.extra && EXTRA_KEY in event.extra && Array.isArray(event.extra[EXTRA_KEY])) {
    return event.extra[EXTRA_KEY];
  }
  return [];
});

Sentry.init({
  dsn: 'https://9f80411ee6ed4d0c8c4701f476721cab@o314572.ingest.sentry.io/6037519',
  environment: Config.stage || process.env.NODE_ENV,
  release: Config.release,
  transport,
  ...getInitialSentryOptions(),
  beforeSend: (event, hint) => {
    const error_message = event.exception?.values?.[0].value ?? '';
    if (isNextNavigationError(error_message)) {
      return null;
    }

    if (isSuspenseBoundaryError(error_message)) {
      return null;
    }

    // MEMO: https://docs.sentry.io/platforms/javascript/best-practices/micro-frontends/
    if (event?.exception?.values?.[0]?.stacktrace?.frames) {
      const frames = event.exception.values[0].stacktrace.frames;
      // Find the last frame with module metadata containing a DSN
      const routeTo = frames
        .filter((frame) => frame.module_metadata && frame.module_metadata.dsn)
        .map((v) => v.module_metadata)
        .slice(-1); // using top frame only - you may want to customize this according to your needs

      if (routeTo.length) {
        event.extra = {
          ...event.extra,
          [EXTRA_KEY]: routeTo,
        };
      }
    }

    try {
      if (
        typeof hint.originalException === 'object' &&
        hint.originalException != null &&
        'message' in hint.originalException &&
        event.exception.values.length > 0
      ) {
        event.exception.values[0].type = hint.originalException.name || 'Error';
        event.exception.values[0].value = hint.originalException.message;

        if (!(hint.originalException instanceof Error)) {
          delete event.exception.values[0].mechanism.synthetic;
        }
      }
    } catch (e) {
      // do nothing
    }

    return event;
  },
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.moduleMetadataIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,
    }),
    Sentry.thirdPartyErrorFilterIntegration({
      filterKeys: ['zigzag-store'],
      behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
    }),
  ],
  ignoreErrors: [
    'Error: Failed to fetch',
    'TypeError: Failed to fetch',
    'Error: Load failed',
    'TypeError: Load failed',
    'Error: NetworkError when attempting to fetch resource.',
    'TypeError: NetworkError when attempting to fetch resource.',
    'Error: cancelled',
    'TypeError: cancelled',
    'Error: 취소됨',
    'TypeError: 취소됨',
    'ReferenceError: NetFunnel is not defined',
    'zaloJSV2', // 베트남 메신저 관련 외부 에러
    '__naverapp__', // 네이버 번역기 관련 외부 에러
    'OcbiOSJS', // OK 캐시백 관련 외부 에러
    'window.animateNavigationBar is not a function', // 서브탭 네비게이션 바 애니메이션 함수 주입 전에 호출된 경우 see also https://croquis.slack.com/archives/C027JSM11SA/p1699327143917199?thread_ts=1697010304.839789&cid=C027JSM11SA
    // https://www.notion.so/croquis/231128-d35f0d164f8948e1a02fa9041ff6675d?pvs=4#4d5f8f9d2ff6497f94e58b90d9892754
    'The object can not be cloned.',
    'ResizeObserver loop completed with undelivered notifications.', // virtuoso ResizeObserver 관련 에러, 성능 이슈 로그 X
    'ResizeObserver loop limit exceeded', // 파이어베이스에서 중복 집계되는 중, 일단 ignore 처리 후 추후 PDP 상품정보 lazy 개선 후 추이 지켜보기
    // https://www.notion.so/croquis/231128-d35f0d164f8948e1a02fa9041ff6675d?pvs=4#4d5f8f9d2ff6497f94e58b90d9892754
    "undefined is not an object (evaluating 'window.webkit.messageHandlers.consoleCaptureError.postMessage')",
    'InvalidStateError: The object is in an invalid state.', // PDP 동영상 재생 시도시 브라우저 정책으로 나는 경고 무시 (화면 크래시 없음)
    'Java object is gone',
    'Java bridge method invocation error',
  ],
  denyUrls: [/extensions\//i, /^chrome:\/\//i],
});
